/**
 * Optimización de imágenes en el browser antes de subirlas a R2.
 *
 * Estrategia:
 *   1. Decodifica el File a una <img> en memoria.
 *   2. Resizea manteniendo aspect ratio. Nunca upscale: si la imagen
 *      original ya es ≤ maxDim, queda igual en tamaño (solo se reencodea).
 *   3. Re-encode como WebP con calidad configurable. WebP suele dar
 *      30-60% menos bytes que JPEG a calidad equivalente.
 *   4. Fallback a JPEG si el browser no soporta WebP toBlob (Safari < 14).
 *
 * Impacto típico: una foto de celular 4032×3024 (~4MB) → 1024×768 WebP
 * (~120-180KB). 95%+ de ahorro sin que el ojo humano note diferencia
 * para un thumbnail de producto en POS.
 *
 * El archivo original NO se sube a R2 — lo descartamos en memoria después
 * de generar el blob optimizado.
 */

export interface OptimizeOptions {
  /** Dimensión máxima (ancho o alto) en px. Default 1024. */
  maxDim?: number;
  /** Calidad WebP/JPEG (0–1). Default 0.82 — buen balance para fotos. */
  quality?: number;
}

export interface OptimizeResult {
  blob: Blob;
  /** Extensión que debe usarse al uploadear (`.webp` o `.jpg`). */
  ext: '.webp' | '.jpg';
  originalBytes: number;
  optimizedBytes: number;
  /** Dimensiones finales tras el resize. */
  width: number;
  height: number;
}

const DEFAULT_MAX_DIM = 1024;
const DEFAULT_QUALITY = 0.82;

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = document.createElement('img');
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo decodificar la imagen'));
    };
    img.src = url;
  });

/**
 * Calcula las dimensiones de salida respetando aspect ratio. Nunca
 * upscale. Exportado para poder testearlo sin canvas.
 */
export const computeTargetSize = (
  originalWidth: number,
  originalHeight: number,
  maxDim: number,
): { width: number; height: number } => {
  const longest = Math.max(originalWidth, originalHeight);
  const scale = Math.min(1, maxDim / longest);
  return {
    width: Math.max(1, Math.round(originalWidth * scale)),
    height: Math.max(1, Math.round(originalHeight * scale)),
  };
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number,
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, mime, quality));

export const optimizeImage = async (
  file: File,
  opts: OptimizeOptions = {},
): Promise<OptimizeResult> => {
  const maxDim = opts.maxDim ?? DEFAULT_MAX_DIM;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const originalBytes = file.size;

  const img = await loadImage(file);
  const { width, height } = computeTargetSize(
    img.naturalWidth,
    img.naturalHeight,
    maxDim,
  );

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D no disponible en este browser');
  }
  // drawImage con 4 args resizea con el smoothing del browser. Para
  // thumbnails de producto el default es suficiente.
  ctx.drawImage(img, 0, 0, width, height);

  // Intenta WebP primero. Si el browser no lo soporta o toBlob devuelve
  // null, cae a JPEG — mismo flujo pero con otra extensión.
  let blob = await canvasToBlob(canvas, 'image/webp', quality);
  let ext: '.webp' | '.jpg' = '.webp';
  if (!blob || blob.size === 0) {
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    ext = '.jpg';
  }
  if (!blob) {
    throw new Error('No se pudo encodear la imagen');
  }

  return {
    blob,
    ext,
    originalBytes,
    optimizedBytes: blob.size,
    width,
    height,
  };
};

/** Formatea bytes como "1.2 MB" / "340 KB" / "900 B" para mostrar en UI. */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
